// Neighbours in their own light, two looks (roadmap 44, sprint 20). Each is a bull knock in every rule (the server
// rolls 'bull': same odds share, x2 if right, no stacking with gold, red, bear or the outbreak x2); only the visitor
// behind the glass, its light and its plate differ. The round message carries `look` beside `special: 'bull'`; a look
// on any other special is ignored. Each visitor is a made-up neighbour from the block.
//   pinch  : Mr Pinch, a hunched host in a long coat with a turned-up collar; where its head should be sits a round
//            eight-legged rider, the legs gripping down onto the shoulders and collar. The spread legs are the widest
//            thing; the shoulders show under them, lit at the rim. Light: a sick pink. Reveal: in three jumps the knees
//            flex higher, the feet clamp in round the collar, the rider tilts as if steering, the shoulders sink.
//   lintel : Mr Lintel, a tall, thin figure bent from one wire, a walking doorway: a square head with two dot eyes, the
//            arch forming its shoulders and long arms down to the floor. Hollow: the beam shows through its body, the
//            only see-through visitor. Light: a cold violet. Reveal: the wire lights segment by segment like a neon
//            sign warming up, then the two dots blink.
// Like the bull (beasts.js): before the reveal the look is the same whoever is behind it; at the reveal (here the final
// step) the eyes tell: an alien's go red, the infected's plague green, a human's stay plain. Stepped at 15 fps.
// Pure; drawGlass (visitors.js) paints it.
import { GLASS } from './visitors.js';
import { T } from './bits.js';
import { pickEye, eyeLook } from './eyes.js';
import { FPS } from './beasts.js';
import { holdenState } from './neighbours-holden.js';

export const LIT_LOOKS = ['pinch', 'lintel'];
export const isLit = s => s === 'pinch' || s === 'lintel';
// the bear's looks (sprint 36 item 3, neighbours-holden.js): a bear knock in another look, on exactly the bear's terms
export const BEAR_LOOKS = ['holden'];
export const isBearLook = s => s === 'holden';
// special: the rules it plays by (the bull's for pinch and lintel, the bear's for holden). name: the made-up neighbour on the
// plate and in the chat; word: the special's step in the sum chalked on the door (x 2 PINCH), the twin of x 2 BULL.
export const LIT_KNOCKS = {
  pinch: { special: 'bull', name: 'Mr Pinch', flat: '1B', word: 'PINCH' },
  lintel: { special: 'bull', name: 'Mr Lintel', flat: '4A', word: 'LINTEL' },
  holden: { special: 'bear', name: 'Mr Holden', flat: '2B', word: 'HOLDEN' },
};
// the look a round shows, or null: a bull look only ever on a bull knock, a bear look only on a bear knock
export const litOf = (special, look) => (special === 'bull' && isLit(look) ? look : special === 'bear' && isBearLook(look) ? look : null);
// the event plate, in the look's own light (the bull's reads 'Bull knock  x2 if right', the bear's 'Bear knock  x2 if wrong')
export const litPlate = look => `${LIT_KNOCKS[look].name}  x2 if ${LIT_KNOCKS[look].special === 'bear' ? 'wrong' : 'right'}`;

const MW = 180, MH = 400, MCX = 90, MOY = 120, FEET = MOY + 250;
const STEPS = [3, 7, 11]; // the reveal's three jumps (after the lamp's stutter), as the bull's
const TELL = 11; // the eyes tell at the final step
export const FLARE = { pinch: [0, 2, 4, 6], lintel: [0, 0, 1, 2] };

// The pose t seconds after the reveal; t < 0 (still knocking) is the same for every kind.
export function litPose(look, kind = null, t = -1) {
  const f = t == null || t < 0 ? -1 : Math.floor(t * FPS + 1e-6);
  const step = f < 0 ? 0 : STEPS.filter(s => f >= s).length;
  const eyes = f >= TELL && kind === 'alien' ? 'red' : f >= TELL && kind === 'infected' ? 'sick' : 'plain';
  if (look === 'pinch') return { step, eyes, k: 1 + 0.015 * step, up: 0, blink: false };
  // lintel: once fully lit the dots blink, off for two frames, then stay on
  return { step, eyes, k: 1, up: 0, blink: f >= TELL + 2 && f < TELL + 4 };
}

const ell = (x, y, cx, cy, ax, ay) => ((x - cx) / ax) ** 2 + ((y - cy) / ay) ** 2 <= 1;
// distance from (x, y) to the segment a-b
function segD(x, y, [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay, l = dx * dx + dy * dy;
  const t = l ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l)) : 0;
  return [Math.hypot(x - ax - t * dx, y - ay - t * dy), t];
}

// Mr Pinch, in mask pixels (at full size one mask pixel is one glass pixel, mask row y on glass row y - 36). The rider
// sits high, so on a phone (the glass cut at about row 133 by tonight's rule) the host's shoulders and collar still show
// under it. HY: the rider's centre; SH: the top of the host's shoulders while knocking; AX, AY: the shoulders' half
// width and height. Each reveal step: the knees rise RISE px and pull in, the feet clamp in round the collar, the rider
// tilts TILT rad about its fore-body, and the shoulders sink SINK px under it.
const PINCH = { hy: 122, sh: 140, ax: 45, ay: 36, rise: 5, tilt: 0.16, sink: 3, kneeIn: 0, footIn: 0.12 };
// the legs, right side (the left mirrors it, before the tilt): root and knee as [dx, dy] from the rider's centre, then
// how far out the foot grips the shoulders. The knees fan out round the body, so the outline is a saw of four sharp
// peaks a side; each leg hooks back down onto the shoulders or the collar. The spread knees are the widest thing.
const LEGS = [
  [[16, 8], [58, 14], 32], // the low pair grips the top of the shoulders, leaving their outer slope bare
  [[17, 0], [62, -10], 27],
  [[14, -7], [44, -19], 21],
  [[9, -12], [30, -23], 15], // the high pair arches over and grips the collar
];
// the top of the host's shoulders at dx from the middle (mask y), with the shoulders sunk to sh
const shoulderTop = (dx, sh) => sh + PINCH.ay - PINCH.ay * Math.sqrt(Math.max(0, 1 - (dx / PINCH.ax) ** 2));
// both sides' legs at a reveal step, in mask pixels: [root, knee, foot]. The roots and knees turn with the rider; the
// feet stay where they grip.
function legsAt(step, turn) {
  const { hy, sh, rise, kneeIn, footIn, sink } = PINCH, out = [];
  for (const sg of [-1, 1]) for (const [r, k, f] of LEGS) {
    const fx = f * (1 - footIn * step), s = sh + sink * step;
    out.push([turn(MCX + sg * r[0], hy + r[1]), turn(MCX + sg * k[0] * (1 - kneeIn * step), hy + k[1] - rise * step),
      [MCX + sg * fx, shoulderTop(fx, s) + 3]]);
  }
  return out;
}

const maskCache = new Map();
// The figure as a mask (the shape silhouetteMask in visitors.js returns): m 1 = the figure; d = detail shade, shown only
// from the first reveal step (2 pale, 3 lit from behind, 4 white-hot: the lintel's lit wire).
export function litMask(look, step = 0) {
  const key = `${look}|${step}`;
  if (maskCache.has(key)) return maskCache.get(key);
  const m = new Uint8Array(MW * MH), d = new Uint8Array(MW * MH);
  let M;
  if (look === 'pinch') {
    // the rider tilts further each step, about its fore-body on the host's neck, as if steering
    const { hy, ax: SAX, ay: SAY } = PINCH, hx = MCX, sh = PINCH.sh + PINCH.sink * step, scy = sh + SAY;
    const a = -PINCH.tilt * step, ca = Math.cos(a), sa = Math.sin(a), PX = hx, PY = hy + 8;
    const unrot = (x, y) => [PX + (x - PX) * ca + (y - PY) * sa, PY - (x - PX) * sa + (y - PY) * ca];
    const rot = (x, y) => [PX + (x - PX) * ca - (y - PY) * sa, PY + (x - PX) * sa + (y - PY) * ca];
    const legs = legsAt(step, rot);
    const GAP = 2.5; // a lit seam between the rider and the coat: the host's shoulder line stays a line under the legs
    for (let y = 0; y < MH; y++) for (let x = 0; x < MW; x++) {
      const i = y * MW + x, dx = x - MCX;
      // the rider, in its own (tilted) frame: a round body, a small fore-body low on it
      const [rx, ry] = unrot(x, y);
      let rider = ell(rx, ry, hx, hy, 17, 14) || ell(rx, ry, hx, hy + 12, 9, 6), knee = false, near = rider;
      if (!near) near = ell(rx, ry, hx, hy, 17 + GAP, 14 + GAP);
      for (const [r, k, f] of legs) {
        if (rider) break;
        const [d1, t1] = segD(x, y, r, k), [d2, t2] = segD(x, y, k, f);
        if (d1 <= 3.4 - 0.6 * t1 || d2 <= 3.0 - 0.6 * t2) { rider = true; knee = Math.hypot(x - k[0], y - k[1]) <= 2.4; }
        else if (d1 <= 3.4 + GAP || (d2 <= 3.0 + GAP - 0.6 * t2 && t2 < 0.8)) near = true; // the foot itself grips
      }
      // the host: hunched shoulders, a turned-up collar of two pointed flaps beside the neck, a long coat
      let host = (y <= scy && ell(x, y, MCX, scy, SAX, SAY)) || (y > scy && y <= FEET && Math.abs(dx) <= SAX + (y - scy) * 0.04);
      if (y >= sh - 13 && y <= sh + 8) { const f = (y - (sh - 13)) / 21, o = 12 + 2 * f; host ||= Math.abs(dx) >= o && Math.abs(dx) <= o + 2 + 11 * f; }
      if (host && !rider && near) host = false;
      if (!rider && !host) continue;
      m[i] = 1;
      if (step >= 1) {
        if (knee) d[i] = 3; // the joints catch the light as they flex
        if (rider && ell(rx, ry, hx, hy - 2, 11, 8) && !ell(rx, ry, hx, hy - 2, 7.5, 5)) d[i] = 2; // a pale ring round its back
        if (!rider && y >= scy && Math.abs(dx) <= 2) d[i] = 2; // the host's coat seam
      }
    }
    const [ex0, ey0] = rot(hx - 7, hy + 3), [ex1, ey1] = rot(hx + 7, hy + 3), [cx, cy] = rot(hx, hy);
    M = { head: [cx, cy, 17, 14], mouth: [cx, cy + 11], eyes: [[ex0, ey0], [ex1, ey1]], nostrils: [] };
  } else {
    // one wire, 9 px across (W2 4.5 either side of its line): the white-hot core 5 px (dist <= 2), an unlit rim of 2 px
    // either side. s runs along the wire from the left
    // foot, up the arm, over the arch, down the right arm, then up the neck and round the head: the order it lights in.
    const R = 42, AY = 160, HW = 17, HT = 74, HB = 102, W2 = 4.5;
    const lit = step / 3;
    const armLen = FEET - AY, archLen = Math.PI * R, neckLen = AY - R - HB, headLen = 4 * 2 * HW;
    const total = 2 * armLen + archLen + neckLen + headLen;
    for (let y = 0; y < MH; y++) for (let x = 0; x < MW; x++) {
      const i = y * MW + x, dx = x - MCX;
      let dist = 9, s = 0;
      if (y >= AY && y <= FEET) { // the arms, down to the floor, the hands turned out a little at the bottom
        const lean = y > FEET - 14 ? (y - (FEET - 14)) * 0.5 : 0;
        const e = Math.abs(Math.abs(dx) - R - lean);
        if (e < dist) { dist = e; s = dx < 0 ? (FEET - y) / total : (armLen + archLen + (y - AY)) / total; }
      }
      if (y < AY + 1) { // the arch: shoulders
        const e = Math.abs(Math.hypot(dx, y - AY) - R);
        if (e < dist) { dist = e; s = (armLen + Math.atan2(AY - y, -dx) * R) / total; }
      }
      if (y >= HB && y <= AY - R + 1 && Math.abs(dx) < dist) { dist = Math.abs(dx); s = (2 * armLen + archLen + (AY - R - y)) / total; } // the neck
      // the square head, its corners clipped
      const ex = Math.abs(Math.abs(dx) - HW), ey = Math.abs(y - (y < (HT + HB) / 2 ? HT : HB));
      const onX = Math.abs(dx) <= HW + W2 && y >= HT - W2 && y <= HB + W2;
      const corner = Math.abs(dx) > HW - 2 && (y < HT + 2 || y > HB - 2) && Math.hypot(Math.abs(dx) - (HW - 2), (y < (HT + HB) / 2 ? HT + 2 : HB - 2) - y) > 4 + W2;
      if (onX && !corner) {
        const e = Math.min(ex, ey);
        if (e < dist) { dist = e; s = (2 * armLen + archLen + neckLen + (Math.atan2(y - (HT + HB) / 2, dx) + Math.PI) / (2 * Math.PI) * headLen) / total; }
      }
      if (dist > W2) continue;
      m[i] = 1;
      if (step >= 1 && dist <= 2 && s < lit + 1e-9) d[i] = 4;
    }
    M = { head: [MCX, (HT + HB) / 2, HW, (HB - HT) / 2], mouth: [MCX, HB - 6], eyes: [[MCX - 7, 86], [MCX + 7, 86]], nostrils: [] };
  }
  let topRow = MH;
  for (let i = 0; i < m.length; i++) if (m[i]) { topRow = Math.floor(i / MW); break; }
  Object.assign(M, { w: MW, h: MH, m, d, cx: MCX, oy: MOY, top: topRow, feet: FEET });
  if (maskCache.size > 32) maskCache.clear();
  maskCache.set(key, M);
  return M;
}

// Everything drawGlass needs for one frame of a lit knock: beastState's fields (beasts.js), the look's own beam.
export function litState({ look, kind = null, revealT = -1, fr = 0, t = 0, outbreak = false, lamp = 1, approach = 1,
  jitter = 0, blink = false, eye = null, slip = false, redEyes = false } = {}) {
  if (isBearLook(look)) return holdenState({ kind, revealT, fr, t, outbreak, lamp, approach, jitter, blink, eye, slip, redEyes });
  const pose = litPose(look, kind, revealT);
  const M = litMask(look, pose.step);
  const base = 0.62 + 0.38 * approach;
  const s = base * pose.k;
  const sway = Math.sin(t * 1.3) * 0.8 + jitter;
  const gs = { lamp, fr, t, outbreak: !!outbreak, glitch: false, snow: 1, ground: false, glow: 0, sores: false, hidden: false,
    M, sx: s, sy: s, ax: GLASS.hx + sway, hv: GLASS.foot - (GLASS.foot - GLASS.hy) * base - pose.up, pres: 0.32 + 0.68 * approach,
    crisp: false, eyes: [], eyesOn: false, eyeTint: T.none, puffs: [], prints: [], puffTint: T.none,
    beam: { beast: look, tint: T[look], flare: FLARE[look][pose.step] } };
  gs.ax = Math.round(gs.ax); // the figure's centre on a whole glass pixel, so the eyes are level and mirror-symmetric
  gs.eyes = M.eyes.map(([x, y]) => [Math.round(gs.ax + (x - M.cx) * s), Math.round(gs.hv + (y - M.oy) * s)]);
  gs.eyesOn = gs.pres > 0.85 && !blink && !pose.blink;
  gs.eyeTint = pose.eyes === 'red' || redEyes ? T.red : pose.eyes === 'sick' || outbreak ? T.green : T.none;
  gs.eye = pickEye('S', eye, eyeLook(gs.eyeTint === T.red, pose.eyes === 'sick', kind, revealT), slip);
  return gs;
}
